import React, { useCallback, useEffect, useState } from "react";
import { Card, Table } from "reactstrap";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

// Outlook add-in pairing (Task 20475).
//
// The add-in used to authenticate with a company-wide API key, which AuthFilter
// resolves to no user at all, so every email it saved was attributed to nobody.
// It now pairs to ONE staff member: you click Connect, get a short code, and type
// that code into the panel in Outlook. The backend swaps it for a device token
// bound to your User_Id, so the add-in acts as you and is permission-checked as you.
//
// Codes last 15 minutes and are single use. Revoking a device deletes its token.
export default function OutlookAddinIntegration() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState([]);
  const [pairing, setPairing] = useState(null); // { PairingCode, PairingExpiryDateTime }
  const [secondsLeft, setSecondsLeft] = useState(0);

  const loadDevices = useCallback(async () => {
    const res = await FetchAPI("/outlookaddin/pairing/list", "GET");
    if (res?.status === 200) {
      setDevices(res.outlookaddindevice_list || []);
    } else if (res) {
      toast.error(res.message || "Could not load paired devices");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Count the code down so it is obvious when it has gone stale, rather than
  // leaving a dead code on screen that silently stops working.
  useEffect(() => {
    if (!pairing?.PairingExpiryDateTime) return undefined;

    const tick = () => {
      // The API returns "YYYY-MM-DD HH:MM:SS"; Safari will not parse that with a
      // space, so normalise to ISO before diffing.
      const expiry = new Date(pairing.PairingExpiryDateTime.replace(" ", "T"));
      const left = Math.max(0, Math.floor((expiry.getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) setPairing(null);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [pairing]);

  const handleConnect = async () => {
    setBusy(true);
    const res = await FetchAPI("/outlookaddin/pairing/create", "POST", []);
    setBusy(false);

    if (res?.status === 200) {
      setPairing({
        PairingCode: res.PairingCode,
        PairingExpiryDateTime: res.PairingExpiryDateTime,
      });
      loadDevices();
    } else {
      toast.error(res?.message || "Could not create a pairing code");
    }
  };

  const handleRevoke = async (device) => {
    setBusy(true);
    const res = await FetchAPI("/outlookaddin/pairing/delete", "DELETE", [
      { MasterTokenKey_Id: device.MasterTokenKey_Id },
    ]);
    setBusy(false);

    if (res?.status === 200) {
      toast.success("Device revoked");
      loadDevices();
    } else {
      toast.error(res?.message || "Could not revoke that device");
    }
  };

  const copyCode = () => {
    if (!pairing?.PairingCode) return;
    navigator.clipboard?.writeText(pairing.PairingCode).then(
      () => toast.success("Code copied"),
      () => toast.error("Could not copy the code")
    );
  };

  const formatCountdown = (total) => {
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Card className="p-3">
      <h5 className="mb-3">Outlook Add-in</h5>

      <p className="text-muted">
        The CRMit panel in Outlook shows who an email is from, lets you add them if
        they are new, and saves the message with the original sender preserved.
        Connecting pairs the add-in to your own CRMit account, so anything it saves
        is recorded against you.
      </p>

      {pairing ? (
        <div className="border rounded p-3 mb-3 text-center">
          <div className="text-muted mb-1">
            Type this into the CRMit panel in Outlook
          </div>
          <div
            className="fw-bold"
            style={{ fontSize: "32px", letterSpacing: "8px" }}
          >
            {pairing.PairingCode}
          </div>
          <div className="text-muted mt-1">
            Expires in {formatCountdown(secondsLeft)}
          </div>
          <div className="d-flex gap-2 justify-content-center mt-3">
            <button className="btn btn-light" onClick={copyCode}>
              Copy code
            </button>
            <button className="btn btn-light" onClick={() => setPairing(null)}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-3">
          <button
            className="btn btn-primary"
            onClick={handleConnect}
            disabled={busy}
          >
            Connect Outlook
          </button>
        </div>
      )}

      <h6 className="mt-2">Paired devices</h6>

      {devices.length === 0 ? (
        <p className="text-muted mb-0">
          No Outlook add-ins are connected for this company yet.
        </p>
      ) : (
        <Table className="table-sm align-middle mb-0">
          <thead>
            <tr>
              <th>User</th>
              <th>Device</th>
              <th>Connected</th>
              <th>Last seen</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.MasterTokenKey_Id}>
                <td>{d.User_FullName || d.User_Email || "-"}</td>
                <td>
                  {d.FlagPending === 1 ? (
                    <span className="text-muted">Waiting for the code</span>
                  ) : (
                    d.DeviceLabel || "Outlook"
                  )}
                </td>
                <td>{d.PairedDateTime || "-"}</td>
                <td>{d.LastSeenDateTime || "-"}</td>
                <td className="text-end">
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => handleRevoke(d)}
                    disabled={busy}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
